//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React, { useState } from 'react';
import { FieldError } from 'react-hook-form';
import styled from 'styled-components';

import { TypeaheadDropdownContent } from '@campaigns/components/shared/product-details-modal/form/TypeaheadDropdownContent';
import { TypeaheadSearchFieldLabel } from '@campaigns/components/shared/product-details-modal/form/TypeaheadSearchFieldLabel';
import { PrimaryLinkButton } from '@components/atoms/button/Button';
import Icon from '@components/atoms/icon/Icon';
import { CaptionErrorText } from '@components/atoms/text/Text';
import { Input } from '@components/atoms/text-input/TextInput';
import DropdownButton from '@components/molecules/button-popover/DropdownButton';
import { DropdownMenu } from '@components/primitives/dropdown-menu/DropdownMenu';
import { TrackingIdPrefix } from '@models/TrackingId';
import { isDefined, isTruthy } from '@utils/filterPredicates';

interface TypeaheadSearchOption {
  externalID: string;
  name: string;
}

interface TypeaheadSearchFieldProps {
  debouncedSearchTerm: string;
  error?: FieldError;
  fieldId: string;
  fieldLabelId: string;
  helpText: string;
  isLoading: boolean;
  labelText: string;
  onSearch: (searchTerm: string) => void;
  onSelect: (value: string) => void;
  options: TypeaheadSearchOption[];
  queryError: Error | null;
  resourceName: string;
  searchKeys?: string[];
  searchPlaceholder: string;
  trackingIdPrefix: TrackingIdPrefix;
  value?: string;
  visuallyHiddenLabel: string;
}

export const TypeaheadSearchField = ({
  debouncedSearchTerm,
  error,
  fieldId,
  fieldLabelId,
  helpText,
  isLoading,
  labelText,
  onSearch,
  onSelect,
  options,
  queryError,
  resourceName,
  searchKeys = ['name'],
  searchPlaceholder,
  trackingIdPrefix,
  value = '',
  visuallyHiddenLabel,
}: TypeaheadSearchFieldProps) => {
  const [isInFreeTextMode, setIsInFreeTextMode] = useState(false);

  const trimmedSearch = debouncedSearchTerm.trim();
  const hasNoOptions = options.length === 0;
  const isExactMatch = options.some(
    (option) => option.name.trim().toLowerCase() === trimmedSearch.toLowerCase(),
  );

  const allowCustomValue =
    isTruthy(trimmedSearch) && !isLoading && !queryError && !isExactMatch;

  const showFreeTextInput =
    (!isLoading && hasNoOptions && !trimmedSearch) || isInFreeTextMode;

  const handleCustomValueSelect = (customValue: string) => {
    setIsInFreeTextMode(true);
    onSelect(customValue);
  };

  const handleSwitchToCatalog = () => {
    setIsInFreeTextMode(false);
    onSearch('');
    onSelect('');
  };

  return (
    <Container>
      <LabelContent htmlFor={fieldId} id={fieldLabelId}>
        <TypeaheadSearchFieldLabel helpText={helpText} labelText={labelText} />
      </LabelContent>
      {showFreeTextInput ? (
        <InputWrapper>
          <StyledInput
            aria-labelledby={fieldLabelId}
            hasError={isDefined(error)}
            id={fieldId}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              onSelect(event.target.value)
            }
            trackingId={`${trackingIdPrefix}text-input`}
            type='text'
            value={value}
          />
          {isInFreeTextMode && (
            <PrimaryLinkButton
              onClick={handleSwitchToCatalog}
              trackingId={`${trackingIdPrefix}search-catalog-link`}
            >
              Search Catalog
            </PrimaryLinkButton>
          )}
        </InputWrapper>
      ) : (
        <DropdownWithButtonWrapper>
          <DropdownMenu>
            <DropdownMenu.Trigger asChild>
              <DropdownButton
                aria-labelledby={fieldLabelId}
                hasError={isDefined(error)}
                id={fieldId}
                isDropdown
                trackingId={`${trackingIdPrefix}trigger`}
                valueText={value}
              />
            </DropdownMenu.Trigger>
            <TypeaheadDropdownContent
              allowCustomValue={allowCustomValue}
              isLoading={isLoading}
              onCustomValueSelect={handleCustomValueSelect}
              onSearch={onSearch}
              onSelect={onSelect}
              options={options}
              queryError={queryError}
              resourceName={resourceName}
              searchKeys={searchKeys}
              searchPlaceholder={searchPlaceholder}
              trackingIdPrefix={trackingIdPrefix}
              trimmedSearch={trimmedSearch}
              value={value}
              visuallyHiddenLabel={visuallyHiddenLabel}
            />
          </DropdownMenu>
        </DropdownWithButtonWrapper>
      )}
      {isDefined(error) && (
        <ErrorWrapper>
          <ErrorIcon name='error' />
          <CaptionErrorText>{error.message}</CaptionErrorText>
        </ErrorWrapper>
      )}
    </Container>
  );
};

const Container = styled.div`
  position: relative;
  width: 50%;
`;

const LabelContent = styled.label(
  ({ theme }) => `
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: ${theme.spacings[2]};
  `,
);

const ErrorWrapper = styled.div(
  ({ theme }) => `
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-top: ${theme.spacings[1]};
  `,
);

const ErrorIcon = styled(Icon)(
  ({ theme }) => `
    width: calc(${theme.spacings[2]} + ${theme.spacings[1]});
    margin-right: ${theme.spacings[1]};
    color: ${theme.colors.aliases.error};
  `,
);

const StyledInput = styled(Input)(
  ({ theme }) => `
    flex: 1;
    padding: ${theme.spacings[2]};
  `,
);

const DropdownWithButtonWrapper = styled.div(
  ({ theme }) => `
    display: flex;
    gap: ${theme.spacings[2]};
    align-items: flex-start;
  `,
);

const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacings[2]};
`;
